/**
 * Open-Meteo Historical Weather API — real climate normals.
 * Free, no API key required, lat/lng → JSON.
 * Replaces Gemini-as-climate-data-source.
 */
import type { DataProvider, ProviderGeoContext, ProviderResult } from './types';
import { CACHE_TTL, fetchWithTimeout } from './types';
import type { ClimateData, TimeSeriesData } from '../../types/siteRag';
import { isPhysicalMonthlyPrecip, MAX_PHYSICAL_MONTHLY_PRECIP_MM } from './climate/precipitationBounds';

/** Stull (2011) wet bulb approximation from T (°C) and RH (%). */
function wetBulb(T: number, RH: number): number {
  return T * Math.atan(0.151977 * Math.sqrt(RH + 8.313659))
    + Math.atan(T + RH)
    - Math.atan(RH - 1.676331)
    + 0.00391838 * Math.pow(RH, 1.5) * Math.atan(0.023101 * RH)
    - 4.686035;
}

export interface ClimateWithTimeSeries extends ClimateData {
  timeSeries: TimeSeriesData[];
}

export const openMeteoProvider: DataProvider<ClimateWithTimeSeries> = {
  id: 'open-meteo',
  name: 'Open-Meteo Historical Weather',
  cacheTtlMs: CACHE_TTL.CLIMATE_NORMALS,
  requiresKey: false,

  async fetch(lat, lng, _apiKey?: string, geo?: ProviderGeoContext): Promise<ProviderResult<ClimateWithTimeSeries>> {
    // CA3: `(lat, lng)` is the boundary centroid when a polygon is drawn
    // (`open-meteo` is in CENTROID_QUERY_PROVIDER_IDS). Climate varies far
    // slower than parcel scale, but querying it at the centroid keeps it on
    // the same effective point as the boundary-sampled elevation — which is
    // exactly the coordinate-drift that produces the 0 m × "Low flood"
    // class of cross-validation contradictions.
    const now = new Date();
    const endYear = now.getFullYear() - 1;
    const startYear = endYear - 9; // 10-year climate normal (WMO standard)
    const startDate = `${startYear}-01-01`;
    const endDate = `${endYear}-12-31`;

    const url = `https://archive-api.open-meteo.com/v1/archive?latitude=${lat}&longitude=${lng}&start_date=${startDate}&end_date=${endDate}&daily=temperature_2m_mean,temperature_2m_max,relative_humidity_2m_mean,precipitation_sum&timezone=auto`;

    const res = await fetchWithTimeout(url, 30000, geo?.signal);
    if (!res.ok) throw new Error(`Open-Meteo: ${res.status} ${res.statusText}`);
    const json = await res.json();

    const dates: string[] = json.daily?.time ?? [];
    const temps: (number | null)[] = json.daily?.temperature_2m_mean ?? [];
    const maxTemps: (number | null)[] = json.daily?.temperature_2m_max ?? [];
    const humidities: (number | null)[] = json.daily?.relative_humidity_2m_mean ?? [];
    const precip: (number | null)[] = json.daily?.precipitation_sum ?? [];

    const validTemps = temps.filter((v): v is number => v != null);
    if (validTemps.length === 0) throw new Error('Open-Meteo returned no temperature data');

    const avgTemp = parseFloat((validTemps.reduce((a, b) => a + b, 0) / validTemps.length).toFixed(1));
    const peakTemp = parseFloat(Math.max(...maxTemps.filter((v): v is number => v != null)).toFixed(1));
    const validHum = humidities.filter((v): v is number => v != null);
    const humidity = validHum.length > 0 ? parseFloat((validHum.reduce((a, b) => a + b, 0) / validHum.length).toFixed(0)) : 60;
    const avgWetBulb = parseFloat(wetBulb(avgTemp, humidity).toFixed(1));

    let waterStress: ClimateData['waterStress'] = 'Moderate';
    if (humidity < 40 && peakTemp > 35) waterStress = 'High';
    else if (humidity > 60 && peakTemp < 30) waterStress = 'Low';

    // Build REAL monthly time series from actual data
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthlyData: { temps: number[]; maxTemps: number[]; hums: number[]; precip: number[]; precipYears: Set<number> }[] =
      months.map(() => ({ temps: [], maxTemps: [], hums: [], precip: [], precipYears: new Set<number>() }));

    for (let i = 0; i < dates.length; i++) {
      // Parse "YYYY-MM-DD" lexically. `new Date(str).getMonth()` is
      // timezone-dependent for date-only ISO strings (parsed as UTC
      // midnight, read back in local time), which smears month-boundary
      // days into the neighbouring bucket in western timezones and would
      // corrupt the distinct-year divisor below.
      const monthIdx = parseInt(dates[i].slice(5, 7), 10) - 1;
      if (!Number.isInteger(monthIdx) || monthIdx < 0 || monthIdx > 11) continue;
      if (temps[i] != null) monthlyData[monthIdx].temps.push(temps[i]!);
      if (maxTemps[i] != null) monthlyData[monthIdx].maxTemps.push(maxTemps[i]!);
      if (humidities[i] != null) monthlyData[monthIdx].hums.push(humidities[i]!);
      if (precip[i] != null) {
        monthlyData[monthIdx].precip.push(precip[i]!);
        monthlyData[monthIdx].precipYears.add(parseInt(dates[i].slice(0, 4), 10));
      }
    }

    const timeSeries: TimeSeriesData[] = months.map((month, i) => {
      const md = monthlyData[i];
      const mAvg = md.temps.length > 0 ? md.temps.reduce((a, b) => a + b, 0) / md.temps.length : avgTemp;
      const mPeak = md.maxTemps.length > 0 ? Math.max(...md.maxTemps) : mAvg + 8;
      const mHum = md.hums.length > 0 ? md.hums.reduce((a, b) => a + b, 0) / md.hums.length : humidity;
      const mWb = wetBulb(mAvg, mHum);
      const coolingEff = Math.max(0.4, Math.min(1.0, 1.0 - (mAvg - 10) * 0.015));
      const mMin = md.temps.length > 0 ? Math.min(...md.temps) : mAvg - 8;
      // Mean monthly total precipitation (mm): sum the daily values for
      // this calendar month across the whole window, then divide by the
      // number of distinct years that contributed observations. The
      // pre-fix raw multi-year sum inflated every month ~10x over the
      // 10-year window (e.g. 13,742 mm/yr for NE Louisiana vs ~1,374
      // mm/yr actual) — physically impossible climate normals.
      const mPrecipRaw = md.precip.length > 0
        ? md.precip.reduce((a, b) => a + b, 0) / Math.max(1, md.precipYears.size)
        : undefined;
      // SOURCE RANGE GUARD (CLAUDE.md §4 / StatePoint-NaN convention): a mean
      // monthly total above MAX_PHYSICAL_MONTHLY_PRECIP_MM is the fingerprint
      // of an aggregation/unit bug (e.g. the years-of-record divisor being 1).
      // Reject it to `undefined` here so a non-physical figure can never enter
      // the time series and silently surface in report charts; the SED
      // compiler additionally guards the annual aggregate.
      const mPrecip = mPrecipRaw != null && isPhysicalMonthlyPrecip(mPrecipRaw)
        ? mPrecipRaw
        : undefined;
      if (mPrecipRaw != null && mPrecip === undefined) {
        console.warn(
          `[open-meteo] Dropped non-physical mean monthly precipitation ` +
          `${mPrecipRaw.toFixed(0)} mm for ${month} (> ${MAX_PHYSICAL_MONTHLY_PRECIP_MM} mm bound) ` +
          `at (${lat}, ${lng}) — likely a years-of-record aggregation error.`,
        );
      }
      return {
        month,
        avgTemp: parseFloat(mAvg.toFixed(1)),
        peakTemp: parseFloat(mPeak.toFixed(1)),
        minTemp: parseFloat(mMin.toFixed(1)),
        wetBulbTemp: parseFloat(mWb.toFixed(1)),
        humidity: parseFloat(mHum.toFixed(0)),
        precipitation: mPrecip != null ? parseFloat(mPrecip.toFixed(1)) : undefined,
        coolingEfficiency: parseFloat(coolingEff.toFixed(2)),
      };
    });

    return {
      data: { avgTemp, peakTemp, avgWetBulb, humidity, waterStress, timeSeries },
      sources: [{
        field: 'climate',
        source: `Open-Meteo Historical Weather API (${startYear}-${endYear} daily data, ${validTemps.length} observations)`,
        url: 'https://open-meteo.com/en/docs/historical-weather-api',
        retrievedAt: now.toISOString(),
      }],
    };
  },
};
